import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import RouteSkeleton from "./components/RouteSkeleton";

const Showcase = lazy(() => import("./pages/Showcase"));
const Auth = lazy(() => import("./pages/Auth"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const InnovationPage = lazy(() => import("./pages/InnovationPage"));
const IdeasPage = lazy(() => import("./pages/IdeasPage"));
const ChallengePage = lazy(() => import("./pages/ChallengePage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const AchievementsPage = lazy(() => import("./pages/AchievementsPage"));
const SkillsPage = lazy(() => import("./pages/SkillsPage"));
const ExpertsPage = lazy(() => import("./pages/ExpertsPage"));
const SmartPage = lazy(() => import("./pages/SmartPage"));
const SmartIdeasPage = lazy(() => import("./pages/SmartIdeasPage"));
const IncubatorPage = lazy(() => import("./pages/IncubatorPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const PartnershipsPage = lazy(() => import("./pages/PartnershipsPage"));
const MentalHealthPage = lazy(() => import("./pages/MentalHealthPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const LayoutTest = lazy(() => import("./pages/LayoutTest"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const DedicationsPage = lazy(() => import("./pages/DedicationsPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const KifahValuesPage = lazy(() => import("./pages/KifahValuesPage"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteSkeleton />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/index" element={<Index />} />
              <Route path="/innovation" element={<InnovationPage />} />
              <Route path="/ideas" element={<IdeasPage />} />
              <Route path="/challenge" element={<ChallengePage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/achievements" element={<AchievementsPage />} />
              <Route path="/skills" element={<SkillsPage />} />
              <Route path="/experts" element={<ExpertsPage />} />
              <Route path="/smart" element={<SmartPage />} />
              <Route path="/smart-ideas" element={<SmartIdeasPage />} />
              <Route path="/incubator" element={<IncubatorPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/partnerships" element={<PartnershipsPage />} />
              <Route path="/mental-health" element={<MentalHealthPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/showcase" element={<Showcase />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/my-account" element={<MyAccount />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/layout-test" element={<LayoutTest />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/dedications" element={<DedicationsPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/kifah-values" element={<KifahValuesPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
